import { useContext, useState, useEffect, useRef } from "react";
import Head from "next/head";
import Link from "next/link";
import algoliasearch from "algoliasearch/lite";
import styles from "./product.module.scss";

import ChevronUp from "../../../components/icons/chevron-up.svg";
import ChevronDown from "../../../components/icons/chevron-down.svg";
import Star from "../../../components/icons/star.svg";

import Strain from "../../../components/product/Strain";
import SaleTag from "../../../components/product/SaleTag";
import Dosage from "../../../components/product/Dosage";
import AddToCart from "../../../components/product/AddToCart";
import Kind from "../../../components/product/Kind";
import Toggle from "../../../components/toggle";
import ProductReviews from '../../../components/product/Reviews';
import ProductSlider from "../../../components/sliders/ProductSlider";
import useMediaQuery from "../../../hooks/useMediaQuery";
import Gallery from "../../../components/product/Gallery";

import { useRouter } from "next/router";
import StoreContext from "../../../context/store";
import { sendGTMEvent } from '@next/third-parties/google';
import { getToken } from '../../../pages/api/token';
import { getProductById } from '../../../pages/api/jane-product/[id]';
import { AlgoliaSearchProvider } from "../../../context/algolia";
import Breadcrumb from "../../../components/breadcrumb";
import FavoriteButton from "../../../components/product/Favorite";

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_KEY
);
const indexName = process.env.NEXT_PUBLIC_ALGOLIA_INDEX;


export async function getServerSideProps({ req, params }) {
  const product_id = params.id;
  const initialStore = req.cookies.JANE_STORE || "117,5981"; // TODO make all stores global. Omit other Jane stores 1979,4398,1983 since we only have 2 Treez stores that they pull from
  const storeIds = initialStore.split(",");
  const storeFilters = storeIds.map((id) => `store_id:${id}`).join(" OR ");

  const algoliaIndex = searchClient.initIndex(indexName);

  // Fetch Algolia results directly
  const algoliaResults = await algoliaIndex.search("", {
    hitsPerPage: 1,
    filters: `product_id:${product_id} AND (${storeFilters})`,
  });

  const numberOfHits = algoliaResults.nbHits || 0;

  let serverProduct = null;

  if (numberOfHits > 0) {
    // Use the first hit from Algolia
    serverProduct = {
      ...algoliaResults.hits[0],
      source: "algolia",
    };
  } else {
    // Fallback to Jane API if no hits
    const token = await getToken();
    const apiProduct = await getProductById(token.access_token, product_id).then(
      (res) => res.data.menu_product
    );

    // Normalize the fields to match Algolia's structure
    serverProduct = {
      ...apiProduct,
      store_notes: apiProduct.store_notes || null,
      source: "api",
    };
  }

  // Normalize API product structure to match Algolia. This is a problem as it doesn't include the full product data from the API
  const normalizedProduct = serverProduct?.product || serverProduct;

  return {
    props: {
      initialStore,
      product_id,
      serverProduct: normalizedProduct,
      numberOfHits,
    },
  };
}

export default function ProductPage({ serverProduct }) {
  const { query, asPath } = useRouter();
  const { store } = useContext(StoreContext);
  const [isAvailable, setIsAvailable] = useState(
    serverProduct.source === "algolia" ? true : false
  ); // Manage stock status
  const isBreakPoint = useMediaQuery(768);
  const [clientProduct, setClientProduct] = useState(null); // Client-side product data
  const hasFetched = useRef(false);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [asPath]);

  // Fetch product data on the client side
  useEffect(() => {
    const fetchProductData = async () => {
      console.log("client fetching product data");
      if (!query.id || !store) return; // Do nothing if there's no product ID in the route or store selected

      try {
        setIsAvailable(null); // Set to loading state during client-side fetch

        // Try fetching product data from Algolia
        const algoliaIndex = searchClient.initIndex(indexName);
        const algoliaResults = await algoliaIndex.search("", {
          hitsPerPage: 1,
          filters: `product_id:${query.id} AND store_id:${store}`,
        });

        if (algoliaResults.nbHits > 0) {
          setClientProduct(algoliaResults.hits[0]); // Update client-side product data
          setIsAvailable(true);
        } else {
          // If no results in Algolia, fallback to Jane API
          const token = await getToken();
          const apiProduct = await getProductById(
            token.access_token,
            query.id
          ).then((res) => res.data.menu_product);

          const normalizedProduct = {
            ...apiProduct,
            source: "api"
          };

          setClientProduct(normalizedProduct); // Update with API data
          setIsAvailable(false);
        }
      } catch (error) {
        console.error("Error fetching product data:", error);
        setIsAvailable(false); // Set to out of stock if there's an error
      }
    };

    if (hasFetched.current) {
      fetchProductData();
    } else {
      hasFetched.current = true; // Mark as fetched after the first render
    }
  }, [query.id, store]);

  // GTM Events
  useEffect(() => {
    sendGTMEvent({
      event: 'view_item',
      value: 1,
      currency: 'USD',
      items: [{ item_id: query.id, item_name: serverProduct.name }],
    });
  }, [query.id]);


  const product = clientProduct || serverProduct;

  // clean up the description data from Treez (Treez prepends the dosage data to the description)
  const storeNotes = cleanTreezDescription(product.store_notes);

  // Normalize photo data
  const photos =
    product.photos?.length > 0
      ? product.photos
      : product.product_photos?.map((photo) => ({
        urls: {
          small: photo.urls.small,
          medium: photo.urls.medium,
          original: photo.urls.original,
          extraLarge: photo.urls.extraLarge,
        },
      })) || [];



  return (
    <div>
      <Head>
        <title>{product.name ? `${product.name} | Purple Lotus` : "Purple Lotus"}</title>
        <meta
          name="description"
          content={
            product.store_notes
              ? storeNotes.substring(0, 160)
              : product.description?.substring(0, 160)
          }
        />
        <meta property="og:title" content={product.name} key="title" />
        <meta
          property="og:image"
          content={
            product.photos?.length
              ? product.photos[0].urls.medium
              : "/fallback-image.jpg"
          }
        />
      </Head>

      <main className={styles.productContainer}>
        <div className="container">
          <Breadcrumb customBreadcrumbs={{ productData: serverProduct }} />
          <div className={styles.product}>
            <Gallery photos={photos} category={product.kind} root_subtype={product.root_subtype} lineage={product.category} id={product.product_id} />
            <div className={styles.product__content}>
              <ProductSaleTag amount={product.special_amount} id={product.special_id} title={product.special_title} />
              <Title title={product.name} />
              <Rating rating={product.aggregate_rating} count={product.review_count} />
              {product.brand && <Brand brand={product.brand} />}
              <div className={styles.product__dosage}>
                <Strain category={product.category} />
                <Kind kind={product.kind} kind_subtype={product.kind_subtype} />
                <Dosage
                  percent_thc={product.percent_thc}
                  percent_cbd={product.percent_cbd}
                  dosage={product.dosage}
                />
              </div>

              {isAvailable === null ? (
                <div className={styles.loadingState}>
                  Checking availability...
                </div>
              ) : isAvailable ? (
                <AlgoliaSearchProvider>
                  <AddToCart hit={product} className={"productPage"} weight={query.weight} />
                </AlgoliaSearchProvider>
              ) : (
                <div className={styles.outOfStockContainer}>
                  <div>Out of Stock</div>
                </div>
              )}

              <Description description={product.description} storeNotes={storeNotes} />
              <Terpenes terpenes={product.terpenes} cannabinoids={product.cannabinoids} />
              <Details hit={product} />
            </div>
          </div>
        </div>
        <div
          className={`${styles.relatedProducts} ${!isBreakPoint ? "container-row" : undefined
            }`}
        >
          {product.brand &&
            <AlgoliaSearchProvider>
              <ProductSlider title={`More from ${product.brand}`} filters={`brand:"${product.brand}"`} productId={product.product_id} />
            </AlgoliaSearchProvider>
          }
        </div>

        <ProductReviews rating={product.aggregate_rating} review_count={product.review_count} />


      </main>
    </div>
  );
}

function Title({ title }) {
  return (
    <div className={styles.product__title}>
      <h1 className={styles.product__name}>
        {title}
      </h1>
      {title
        .toLowerCase()
        .match(/medical (only|patients only)/i) && (
          <span className="medical-only">Medical Patients Only</span>
        )}
    </div>
  )
}

function Brand({ brand }) {
  return (
    <Link
      href={"/brands/" + brand.replace(/\s+/g, '-')}
      className={styles.product__brand}
      prefetch={false}
      passHref
      aria-label={brand || "brand"}
    >
      <span>{brand}</span>
    </Link>
  )
}

function ProductSaleTag({ amount, title, id }) {
  return (
    amount != null && (
      <div className={styles.product__sale}>
        <SaleTag
          special_amount={amount}
          special_title={title}
          special_id={id}
        />
      </div>
    )
  );
}

// remove the dosage data in the description
function cleanTreezDescription(data) {
  // Check if data is null or undefined, return empty string if true
  if (!data) return '';

  const lastSeparatorIndex = data.lastIndexOf("--");
  if (lastSeparatorIndex !== -1) {
    return data.substring(lastSeparatorIndex + 2).trim();
  }
  return data;
}

function Description({ description, storeNotes }) {
  const fullText =
    storeNotes && storeNotes.length >= 60
      ? storeNotes.replace(/  /g, "\n\n")
      : description?.replace(/  /g, "\n\n") || "";

  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current && !isExpanded) {
      const isOverflowing =
        textRef.current.scrollHeight > textRef.current.clientHeight;
      setShouldShowToggle(isOverflowing);
    }
  }, [fullText, isExpanded]);

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className={styles.descriptionContainer}>
      <div
        ref={textRef}
        className={`${styles.descriptionText} ${isExpanded ? styles.expanded : styles.collapsed}`}
      >
        {fullText}
      </div>

      {(shouldShowToggle || isExpanded) && (
        <button className={styles.readMore} onClick={handleToggle}>
          {isExpanded ? "Hide" : "Read More"}{" "}
          {isExpanded ? <ChevronUp /> : <ChevronDown />}
        </button>
      )}
    </div>
  );
}

function Terpenes({ terpenes, cannabinoids }) {
  return (
    terpenes || cannabinoids &&
    <Toggle title="Cannabinoids & Terpenes">
      <p>Terpenes: {terpenes}</p>
      <p>Cannabionoids: {cannabinoids}</p>
    </Toggle>
  )
}

function Details({ hit }) {
  return (
    <Toggle title="Details">
      <div className={styles.details}>
        {hit.brand && (
          <div>
            <span className="fw-semi-bold">Brand: </span>
            <Link className="link" href={"/brands/" + hit.brand.replace(/\s+/g, '-')}>{hit.brand}</Link>
          </div>
        )}
        {hit.kind && (
          <div>
            <span className="fw-semi-bold">Category: </span>
            <Link className="link capitalize" href={`/shop/${hit.kind}`}>{hit.kind}</Link>
          </div>
        )}
        {hit.root_subtype && (
          <div>
            <span className="fw-semi-bold">Subcategory:</span>{" "}
            <Link className="link" href={`/shop/${hit.kind}?subcategory=${hit.kind}:${hit.root_subtype}`}>{hit.root_subtype}</Link>
          </div>
        )}
        {hit.kind_subtype && (
          <div>
            <span className="fw-semi-bold">Sub Type:</span>{" "}
            <Link className="link" href={`/shop/${hit.kind}?q=${hit.kind_subtype}`}>{hit.kind_subtype}</Link>
          </div>
        )}
        {hit.category && (
          <div>
            <span className="fw-semi-bold">Strain Type:</span>{" "}
            <Link className="link capitalize" href={`/shop?strain=${hit.category}`}>{hit.category}</Link>
          </div>
        )}
        {hit.percent_thc && (
          <div>
            <span className="fw-semi-bold">THC:</span>{" "}
            {hit.percent_thc}%
          </div>
        )}
        {hit.percent_cbd && (
          <div>
            <span className="fw-semi-bold">CBD:</span>{" "}
            {hit.percent_cbd}%
          </div>
        )}
        {hit.dosage && (
          <div>
            <span className="fw-semi-bold">Dosage:</span> {hit.dosage}
          </div>
        )}

        {hit.terpenes && hit.terpenes.length > 0 && (
          <div>
            <p>Terpenes:</p>
            <div className={styles.terpenes}>
              {hit.terpenes.map((terpene, index) => (
                <p key={index} className={styles.terpene}>
                  {terpene}
                </p>
              ))}
            </div>
          </div>
        )}
        {hit.cannabinoids && hit.cannabinoids.length > 0 && (
          <div>
            <p>Cannabinoids:</p>
            <div className={styles.cannabinoids}>
              {hit.cannabinoids.map((cannabinoid, index) => (
                <p key={index} className={styles.cannabinoid}>
                  {cannabinoid}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* {hit.feelings && hit.feelings.length > 0 && (
        <div>
          <p>Feelings:</p>
          <div className={styles.feelings}>
            {hit.feelings.map((feeling, index) => (
              <div key={index} className={styles.feeling}>
                {feeling}
              </div>
            ))}
          </div>
        </div>
      )} */}
    </Toggle>
  )
}

function Rating({ rating, count }) {

  if (!rating) {
    return null;
  }

  return (
    <div className={styles.rating}>
      <span>{rating}</span>
      <Star />
      <Link
        href="#reviews"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" });
        }}
        className={styles.count}
      >
        ({count} reviews)
      </Link>
    </div>
  )
}